'use client';

import { FC, useState, useMemo, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField, FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { FormLabel } from '@/components/common/FormLabel';
import { OptionKey, KeyValueType } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useDialogStore, type BulkSampleDialogResult } from '@/stores/dialogStore';

import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { StatusDto, type SampleSearchCriteria } from '@/api/ApiClient';
import { useAuthStore } from '@/stores/authStore';
import { LABEL_SUFFIX_OPTIONAL, LABEL_SUFFIX_REQUIRED } from '@/lib/formLabels';
import { canCreateWithoutProject } from '@/lib/auth/unassignedSampleAccess';
import { getProblemDetail } from '@/lib/utils/apiErrors';

const OPTION_KEYS: OptionKey[] = ['projects', 'researcherOptions', 'synthesisTechniquesLeafOnly'];

// Recipe template options matching BulkConstants.Templates
export interface RecipeTemplateOption {
  id: string;
  label: string;
  technique: string;
}

const ALL_RECIPE_TEMPLATES: RecipeTemplateOption[] = [
  {
    id: 'Raw Materials',
    label: 'Prepare Raw Materials (for Compounding, Melt or Flux Growth)',
    technique: 'CMP',
  },
  {
    id: 'Raw Material without Ampoule',
    label: 'Prepare Raw Material Rod for Floating Zone',
    technique: 'Floating Zone',
  },
  {
    id: 'Raw Materials and Transport Agent',
    label: 'Prepare Raw Materials and Transport Agent',
    technique: 'CVT',
  },
  {
    id: 'Raw Materials and Flux Liquid',
    label: 'Prepare Raw Materials and Flux Liquids',
    technique: 'H Flux',
  },
  {
    id: 'Transport Agent',
    label: 'Use precompounded, add Transport Agent',
    technique: 'CVT',
  },
  {
    id: 'Liquid',
    label: 'Use precompounded, add Flux Liquid',
    technique: 'H Flux',
  },
];

/**
 * Dialog for creating new bulk samples or preparing samples
 * Based on NewBulkSamplePopup from Knockout implementation
 */
export const NewBulkSampleDialog: FC = () => {
  const { bulkSampleDialog, closeBulkSampleDialog } = useDialogStore();
  const backend = useBackendStore((state) => state.getBackend());
  const { options } = useSelectOptions(OPTION_KEYS);
  const { showSnackbar } = useSnackbar();
  const isAuthorized = useAuthStore((s) => s.isAuthorized);
  const allowCreateWithoutProject = canCreateWithoutProject(isAuthorized);
  const { showProjectSelectionDialog, showSampleSelectionDialog } = useDialogStore();

  const {
    title,
    initialProject,
    canChangeProject = true,
    showSampleID = true,
    showPrecompoundedSample = true,
    hideRecipeTemplate = false,
    initialRecipeTemplate,
    initialDescription,
    initialSampleLabel,
    initialTechnique,
    availableRecipeTemplates = ALL_RECIPE_TEMPLATES,
  } = bulkSampleDialog;
  const isOpen = bulkSampleDialog.isOpen;

  // Compute initial recipe template
  const initialRecipeTemplateOption = useMemo(() => {
    if (initialRecipeTemplate) {
      return availableRecipeTemplates.find((t) => t.id === initialRecipeTemplate) || null;
    }
    return availableRecipeTemplates.find((t) => t.id === 'Raw Materials') || availableRecipeTemplates[0] || null;
  }, [initialRecipeTemplate, availableRecipeTemplates]);

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedProject, setSelectedProject] = useState<{ id: number; label: string } | null>(initialProject || null);
  const [selectedStaff, setSelectedStaff] = useState<KeyValueType | null>(() => {
    return options.researcherOptions?.[0] || null;
  });
  const [sampleLabel, setSampleLabel] = useState<string>(initialSampleLabel || '');
  const [description, setDescription] = useState<string>(initialDescription || '');
  const [selectedRecipeTemplate, setSelectedRecipeTemplate] = useState<RecipeTemplateOption | null>(
    initialRecipeTemplateOption
  );
  const [precompoundedLabel, setPrecompoundedLabel] = useState<string>('');
  const [sampleLabelError, setSampleLabelError] = useState<string>('');

  // Error states
  const [projectError, setProjectError] = useState(false);
  const [staffError, setStaffError] = useState(false);
  const [recipeTemplateError, setRecipeTemplateError] = useState(false);

  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  // Check if precompounded sample selection is needed (and shown: single-sample flow only)
  const needsPrecompoundedSample = useMemo(() => {
    const templateNeedsIt = selectedRecipeTemplate?.id === 'Transport Agent' || selectedRecipeTemplate?.id === 'Liquid';
    return !!templateNeedsIt && showPrecompoundedSample;
  }, [selectedRecipeTemplate, showPrecompoundedSample]);

  // Validate sample label - check on submit or when user explicitly validates
  const validateSampleLabel = useCallback(
    async (label: string): Promise<boolean> => {
      if (!label || !needsPrecompoundedSample) {
        setSampleLabelError('');
        return true;
      }

      // For precompounded templates, check if sample exists
      try {
        const isUsed = await backend.sample.isLabelUsed(label);
        if (isUsed) {
          setSampleLabelError('');
          return true;
        } else {
          setSampleLabelError(`A sample with ID ${label} could not be found`);
          return false;
        }
      } catch (_error) {
        // Sample doesn't exist
        setSampleLabelError(`A sample with ID ${label} could not be found`);
        return false;
      }
    },
    [needsPrecompoundedSample, backend.sample]
  );

  // Project selection handler
  const handleProjectLookup = useCallback(async () => {
    const projects = await showProjectSelectionDialog('Select Project');
    if (projects && projects.length > 0) {
      const project = projects[0];
      setSelectedProject({ id: project.id!, label: project.projectLabel! });
      setProjectError(false);
      markAsInteracted();
    }
  }, [showProjectSelectionDialog, markAsInteracted]);

  // Precompounded sample lookup handler — seed sample search with project, Compounding technique, and not shipped.
  const handleSampleLookup = useCallback(async () => {
    const initialFilters: Partial<SampleSearchCriteria> = {
      shipped: 'no',
    };
    if (selectedProject?.label) {
      initialFilters.projectId = selectedProject.label;
    }
    const compounding = options.synthesisTechniquesLeafOnly?.find(
      (t) => (t.name ?? '').trim().toLowerCase() === 'compounding'
    );
    if (compounding?.id) {
      initialFilters.techniqueId = compounding.id;
    }
    const samples = await showSampleSelectionDialog('Select Precompounded Sample', undefined, initialFilters);
    if (samples && samples.length > 0) {
      const sample = samples[0];
      setPrecompoundedLabel(sample.sampleLabel || '');
      markAsInteracted();
    }
  }, [showSampleSelectionDialog, markAsInteracted, selectedProject, options.synthesisTechniquesLeafOnly]);

  // Get technique from selected recipe template
  const getTechnique = useCallback((): string => {
    return selectedRecipeTemplate?.technique || '';
  }, [selectedRecipeTemplate]);

  // Reset form and close
  const handleClose = useCallback(() => {
    // Clean up state when closing
    setSelectedDate(new Date());
    setSelectedProject(initialProject || null);
    setSelectedStaff(options.researcherOptions?.[0] || null);
    setSampleLabel(initialSampleLabel || '');
    setDescription(initialDescription || '');
    setSelectedRecipeTemplate(initialRecipeTemplateOption);
    setPrecompoundedLabel('');
    setSampleLabelError('');
    setProjectError(false);
    setStaffError(false);
    setRecipeTemplateError(false);
    closeBulkSampleDialog(null);
  }, [
    closeBulkSampleDialog,
    initialProject,
    initialSampleLabel,
    initialDescription,
    initialRecipeTemplateOption,
    options.researcherOptions,
  ]);

  const handleSubmit = useCallback(async () => {
    let hasError = false;

    if (!allowCreateWithoutProject && !selectedProject) {
      setProjectError(true);
      hasError = true;
    } else {
      setProjectError(false);
    }

    if (!selectedStaff) {
      setStaffError(true);
      hasError = true;
    } else {
      setStaffError(false);
    }

    if (!hideRecipeTemplate && !selectedRecipeTemplate) {
      setRecipeTemplateError(true);
      hasError = true;
    } else {
      setRecipeTemplateError(false);
    }

    if (needsPrecompoundedSample && !precompoundedLabel && !sampleLabel) {
      setSampleLabelError('Please select a precompounded sample');
      hasError = true;
    } else if (needsPrecompoundedSample && (precompoundedLabel || sampleLabel)) {
      // Validate that the precompounded sample exists
      const labelToValidate = precompoundedLabel || sampleLabel;
      const isValid = await validateSampleLabel(labelToValidate);
      if (!isValid) {
        hasError = true;
      }
    }

    if (hasError || sampleLabelError) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    if (!selectedDate) {
      showSnackbar('Please select a date.', 'error');
      return;
    }

    if (!selectedStaff) {
      showSnackbar('Please select a staff member.', 'error');
      return;
    }

    if (!allowCreateWithoutProject && (!selectedProject || !selectedProject.label)) {
      showSnackbar('Please select a project.', 'error');
      return;
    }

    try {
      const result: BulkSampleDialogResult = {
        date: selectedDate,
        description: description || undefined,
        project: selectedProject?.label ?? '',
        sampleLabel: showSampleID ? sampleLabel || undefined : undefined,
        staff: selectedStaff.key || selectedStaff.value || '',
        technique: initialTechnique || getTechnique(),
        template: selectedRecipeTemplate?.id || initialRecipeTemplate || 'Raw Materials',
        precompounded:
          needsPrecompoundedSample && (precompoundedLabel || sampleLabel)
            ? [precompoundedLabel || sampleLabel]
            : undefined,
      };

      closeBulkSampleDialog(result);
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to prepare bulk sample.', 'error');
    }
  }, [
    selectedProject,
    selectedStaff,
    selectedRecipeTemplate,
    selectedDate,
    description,
    sampleLabel,
    precompoundedLabel,
    showSampleID,
    hideRecipeTemplate,
    needsPrecompoundedSample,
    validateSampleLabel,
    sampleLabelError,
    initialTechnique,
    initialRecipeTemplate,
    getTechnique,
    showSnackbar,
    closeBulkSampleDialog,
    allowCreateWithoutProject,
  ]);

  return (
    <StandardDialog
      open={isOpen}
      onClose={handleClose}
      title={title || 'Add Sample'}
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Add Sample"
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            width: '100%',
            alignItems: 'start',
          }}
        >
          {/* Project and Sample ID */}
          <Box sx={{ gridColumn: showSampleID ? '1' : '1 / -1' }}>
            <Box
              sx={{
                opacity: !canChangeProject ? 0.6 : 1,
                pointerEvents: !canChangeProject ? 'none' : 'auto',
              }}
            >
              <EntityAutocompleteWithLookup
                fullWidth
                label={`Project ID${allowCreateWithoutProject ? LABEL_SUFFIX_OPTIONAL : LABEL_SUFFIX_REQUIRED}`}
                options={
                  options.projects
                    ?.filter((p) => p.id && p.projectLabel && p.status === StatusDto.Submitted)
                    ?.map((p) => ({ id: p.id!, label: p.projectLabel! })) ?? []
                }
                value={selectedProject}
                onChange={(newValue) => {
                  if (!canChangeProject) return;
                  const project = newValue as { id: number; label: string } | null;
                  setSelectedProject(project);
                  if (project) setProjectError(false);
                  markAsInteracted();
                }}
                onLookupClick={canChangeProject ? handleProjectLookup : () => {}}
                placeholder={allowCreateWithoutProject ? 'No project' : undefined}
                error={projectError}
                helperText={projectError ? 'Project is required.' : undefined}
                lookupTooltip="Search and select project"
              />
            </Box>
          </Box>
          {showSampleID ? (
            <Box>
              <FormTextField
                label="Sample ID"
                value={sampleLabel}
                onChange={(e) => {
                  setSampleLabel(e.target.value);
                  markAsInteracted();
                }}
                placeholder="<will be generated>"
                fullWidth
                error={!!sampleLabelError}
                helperText={sampleLabelError}
              />
            </Box>
          ) : null}

          {/* Created by and Date - equal columns aligned with row above */}
          <Box>
            <FormAutocomplete
              options={options.researcherOptions ?? []}
              getOptionLabel={(option) => option?.value || ''}
              value={selectedStaff}
              onChange={(_, newValue) => {
                setSelectedStaff(newValue);
                markAsInteracted();
              }}
              label="Created by"
              required
              error={staffError}
              fullWidth
            />
          </Box>
          <Box>
            <FormDatePicker
              label="Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
              required
            />
          </Box>

          {/* Recipe Template */}
          {!hideRecipeTemplate ? (
            <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
              <FormAutocomplete
                options={availableRecipeTemplates}
                getOptionLabel={(option) => option?.label || ''}
                value={selectedRecipeTemplate}
                onChange={(_, newValue) => {
                  setSelectedRecipeTemplate(newValue);
                  // Clear precompounded sample when template changes
                  if (newValue?.id !== 'Transport Agent' && newValue?.id !== 'Liquid') {
                    setPrecompoundedLabel('');
                    setSampleLabel('');
                  }
                  markAsInteracted();
                }}
                label="Recipe Template"
                required={!hideRecipeTemplate}
                error={recipeTemplateError}
              />
            </Box>
          ) : null}

          {/* Precompounded Sample Selection - only when single sample (multiple use selected samples) */}
          {needsPrecompoundedSample ? (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FormTextField
                label="Precompounded Sample ID"
                required
                value={precompoundedLabel || sampleLabel}
                onChange={(e) => {
                  const value = e.target.value;
                  if (sampleLabel) {
                    setSampleLabel(value);
                  } else {
                    setPrecompoundedLabel(value);
                  }
                  markAsInteracted();
                }}
                placeholder="Sample ID"
                fullWidth
                error={!!sampleLabelError}
                helperText={sampleLabelError || 'Enter or select a precompounded sample'}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={handleSampleLookup}
                      aria-label="Search and select precompounded sample"
                      title="Search and select precompounded sample"
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          ) : null}

          {/* Description */}
          <Box sx={{ gridColumn: '1 / -1', mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              <FormLabel optional>Description</FormLabel>
            </Typography>
            <RichTextEditor
              value={description}
              onChange={(value) => {
                setDescription(value);
                markAsInteracted();
              }}
            />
          </Box>
        </Box>
      </LocalizationProvider>
    </StandardDialog>
  );
};
